import { Graphics, type Renderer as PixiRenderer, type Texture } from 'pixi.js';
import { BUILDING_DEFS } from '../sim/constants';
import type { BuildingType } from '../sim/types';
import { drawBuilding, type BuildingMeta } from './buildingArt';
import { HALF_H, HALF_W } from './iso';

const TILE_DRAW_W = 64;
const TILE_DRAW_H = 32;

/**
 * Runtime-generated textures. Terrain shapes are drawn white and tinted by
 * depth; buildings are painted in real colors by buildingArt.
 */
export interface BuildingTexture {
  texture: Texture;
  /** Offset from the footprint's north grid vertex to the texture's top-left. */
  offsetX: number;
  offsetY: number;
  /** Wire attachment point, V-space (relative to the north grid vertex). */
  attachX: number;
  attachY: number;
  anim: BuildingMeta['anim'];
}

export class GrayboxTextures {
  readonly floor: Texture;
  readonly floorVariants: Texture[];
  readonly faceSW: Texture;
  readonly faceSE: Texture;
  readonly diamondOutline: Texture;
  readonly diamondFill: Texture;
  readonly glintIron: Texture;
  readonly glintGold: Texture;
  readonly road: Texture;
  readonly pipe: Texture;
  readonly orePile: Texture;
  readonly floorReclaimed: Texture;
  readonly goodsPile: Texture;
  readonly cattlePile: Texture;
  private readonly renderer: PixiRenderer;
  private readonly buildingCache = new Map<string, BuildingTexture>();
  private readonly peepCache = new Map<string, Texture>();
  private readonly accessoryCache = new Map<string, Texture>();

  constructor(renderer: PixiRenderer) {
    this.renderer = renderer;
    // Sand floors: three dithered variants; speckles are translucent black so
    // the per-depth tint shades them along with the base diamond.
    const floorVariant = (speckles: readonly (readonly [number, number])[]): Texture =>
      this.make((g) => {
        g.poly([HALF_W, 0, TILE_DRAW_W, HALF_H, HALF_W, TILE_DRAW_H, 0, HALF_H]).fill(0xffffff);
        g.poly([HALF_W, 0, TILE_DRAW_W, HALF_H, HALF_W, TILE_DRAW_H, 0, HALF_H]).stroke({ width: 1, color: 0x000000, alpha: 0.07 });
        for (const [x, y] of speckles) {
          g.rect(x, y, 2, 1).fill({ color: 0x000000, alpha: 0.1 });
        }
      });
    this.floorVariants = [
      floorVariant([[20, 10], [38, 16], [30, 24], [45, 14], [26, 18]]),
      floorVariant([[24, 14], [42, 11], [33, 20], [18, 15], [36, 25]]),
      floorVariant([[28, 9], [22, 20], [40, 21], [48, 16], [31, 15]]),
    ];
    this.floor = this.floorVariants[0]!;
    // One-level wall faces hanging from a floor's SW / SE edges. One extra px
    // of drop closes the seam against the deeper floor (which draws later).
    // Horizontal strata bands read as sediment layers once depth-tinted.
    this.faceSW = this.make((g) => {
      g.poly([0, 0, HALF_W, HALF_H, HALF_W, HALF_H + 17, 0, 17]).fill(0xffffff);
      for (const [t, alpha] of [[4, 0.12], [9, 0.2], [13, 0.09]] as const) {
        g.poly([0, t, HALF_W, HALF_H + t, HALF_W, HALF_H + t + 2, 0, t + 2]).fill({ color: 0x000000, alpha });
      }
    });
    this.faceSE = this.make((g) => {
      g.poly([0, HALF_H, HALF_W, 0, HALF_W, 17, 0, HALF_H + 17]).fill(0xffffff);
      for (const [t, alpha] of [[4, 0.12], [9, 0.2], [13, 0.09]] as const) {
        g.poly([0, HALF_H + t, HALF_W, t, HALF_W, t + 2, 0, HALF_H + t + 2]).fill({ color: 0x000000, alpha });
      }
    });
    this.diamondOutline = this.make((g) => {
      g.poly([HALF_W, 1, TILE_DRAW_W - 2, HALF_H, HALF_W, TILE_DRAW_H - 1, 2, HALF_H]).stroke({ width: 2, color: 0xffffff });
    });
    this.diamondFill = this.make((g) => {
      g.poly([HALF_W, 0, TILE_DRAW_W, HALF_H, HALF_W, TILE_DRAW_H, 0, HALF_H]).fill(0xffffff);
    });
    this.glintIron = this.make((g) => {
      g.poly([HALF_W, 0, TILE_DRAW_W, HALF_H, HALF_W, TILE_DRAW_H, 0, HALF_H]).fill({ color: 0xffffff, alpha: 0.001 });
      for (const [x, y] of [[26, 12], [36, 18], [30, 22], [40, 13]] as const) {
        g.rect(x, y, 3, 2).fill(0x54423a);
      }
    });
    this.glintGold = this.make((g) => {
      g.poly([HALF_W, 0, TILE_DRAW_W, HALF_H, HALF_W, TILE_DRAW_H, 0, HALF_H]).fill({ color: 0xffffff, alpha: 0.001 });
      for (const [x, y] of [[27, 13], [37, 17], [31, 21], [41, 12]] as const) {
        g.rect(x, y, 3, 2).fill(0xf2c14e);
      }
    });
    this.road = this.make((g) => {
      g.poly([HALF_W, 4, TILE_DRAW_W - 8, HALF_H, HALF_W, TILE_DRAW_H - 4, 8, HALF_H]).fill({ color: 0xffffff, alpha: 0.9 });
    });
    this.pipe = this.make((g) => {
      g.poly([HALF_W, 0, TILE_DRAW_W, HALF_H, HALF_W, TILE_DRAW_H, 0, HALF_H]).fill({ color: 0xffffff, alpha: 0.001 });
      g.poly([12, HALF_H - 2, HALF_W, 8, TILE_DRAW_W - 12, HALF_H - 2, HALF_W, HALF_H + 4]).stroke({ width: 3, color: 0x4d9ac9 });
    });
    this.orePile = this.make((g) => {
      g.poly([0, 8, 8, 0, 16, 8, 13, 12, 3, 12]).fill(0xffffff);
    });
    // Reclaimed ground: same diamond, plow furrows along the NE axis. Drawn
    // white so the depth tint (always surface here) still applies.
    this.floorReclaimed = this.make((g) => {
      g.poly([HALF_W, 0, TILE_DRAW_W, HALF_H, HALF_W, TILE_DRAW_H, 0, HALF_H]).fill(0xffffff);
      g.poly([HALF_W, 0, TILE_DRAW_W, HALF_H, HALF_W, TILE_DRAW_H, 0, HALF_H]).stroke({ width: 1, color: 0x000000, alpha: 0.07 });
      for (const t of [0.25, 0.5, 0.75]) {
        g.moveTo(HALF_W * t, HALF_H * (1 - t))
          .lineTo(HALF_W * (1 + t), HALF_H * (2 - t))
          .stroke({ width: 2, color: 0x000000, alpha: 0.13 });
      }
      for (const [x, y] of [[22, 13], [34, 19], [41, 15]] as const) {
        g.rect(x, y, 2, 1).fill({ color: 0x000000, alpha: 0.14 });
      }
    });
    // Goods pile: a pair of grain sacks, white for per-good tinting.
    this.goodsPile = this.make((g) => {
      g.ellipse(5, 9, 5, 3.6).fill(0xffffff);
      g.ellipse(11, 9.5, 5, 3.2).fill(0xffffff);
      g.ellipse(8, 5, 4.5, 3.4).fill(0xffffff);
      g.moveTo(8, 2).lineTo(8, 4).stroke({ width: 2, color: 0x000000, alpha: 0.25 });
      g.moveTo(2, 8).lineTo(6, 10).stroke({ width: 1, color: 0x000000, alpha: 0.15 });
      g.moveTo(10, 10).lineTo(14, 9).stroke({ width: 1, color: 0x000000, alpha: 0.15 });
    });
    // Cattle pile: a tiny holding pen, self-colored — a tinted sack silhouette
    // reads as grain, and live cargo deserves better.
    this.cattlePile = this.make((g) => {
      for (const x of [0.5, 8.5, 16.5]) g.rect(x, 2, 1.5, 9).fill(0x5a4630);
      g.moveTo(0, 4).lineTo(18, 4).stroke({ width: 1.5, color: 0x6e5a42 });
      const miniCow = (cx: number, cy: number, tone: number): void => {
        g.ellipse(cx, cy, 4, 2.4).fill(tone);
        g.ellipse(cx + 3.4, cy - 1, 1.6, 1.4).fill(0x6e523a);
        g.moveTo(cx + 2, cy - 2.4).lineTo(cx + 5, cy - 2.4).stroke({ width: 1, color: 0xe4d5b0 });
        for (const lx of [-2.5, -0.5, 1.5]) g.rect(cx + lx, cy + 1.8, 1, 2.4).fill(0x5a4630);
      };
      miniCow(5, 7, 0x8a6a4a);
      miniCow(12, 8.5, 0x9a7a56);
    });
  }

  private make(draw: (g: Graphics) => void): Texture {
    const g = new Graphics();
    draw(g);
    const texture = this.renderer.generateTexture({ target: g, antialias: false });
    g.destroy();
    return texture;
  }

  /** 12x20 billboard walker: body = job color, hat brim = tier badge. */
  peep(bodyColor: number, frame: 0 | 1 | 2 = 0, hatColor = 0x8a6a3a): Texture {
    const key = `${bodyColor}:${frame}:${hatColor}`;
    const cached = this.peepCache.get(key);
    if (cached) return cached;
    const texture = this.make((g) => {
      g.rect(3, 0, 6, 2).fill(hatColor);
      g.rect(2, 2, 8, 4).fill(0xf0d0b0);
      g.rect(1, 6, 10, 10).fill(bodyColor);
      g.rect(1, 6, 10, 2).fill({ color: 0x000000, alpha: 0.15 }); // collar shade
      if (frame === 0) {
        g.rect(2, 16, 3, 4).fill(0x3a2f28);
        g.rect(7, 16, 3, 4).fill(0x3a2f28);
      } else if (frame === 1) {
        g.rect(1, 16, 3, 4).fill(0x3a2f28);
        g.rect(7, 16, 3, 3).fill(0x3a2f28);
      } else {
        g.rect(2, 16, 3, 3).fill(0x3a2f28);
        g.rect(8, 16, 3, 4).fill(0x3a2f28);
      }
    });
    this.peepCache.set(key, texture);
    return texture;
  }

  /** 9x14 child walker. */
  kid(bodyColor: number, frame: 0 | 1 | 2 = 0): Texture {
    const key = `kid:${bodyColor}:${frame}`;
    const cached = this.peepCache.get(key);
    if (cached) return cached;
    const texture = this.make((g) => {
      g.rect(2, 0, 5, 4).fill(0xf0d0b0);
      g.rect(1, 4, 7, 6).fill(bodyColor);
      if (frame === 0) {
        g.rect(2, 10, 2, 3).fill(0x3a2f28);
        g.rect(5, 10, 2, 3).fill(0x3a2f28);
      } else if (frame === 1) {
        g.rect(1, 10, 2, 3).fill(0x3a2f28);
        g.rect(5, 10, 2, 2).fill(0x3a2f28);
      } else {
        g.rect(2, 10, 2, 2).fill(0x3a2f28);
        g.rect(6, 10, 2, 3).fill(0x3a2f28);
      }
    });
    this.peepCache.set(key, texture);
    return texture;
  }

  /** Per-type building art from buildingArt.ts; offsets from actual bounds. */
  building(type: BuildingType): BuildingTexture {
    const cached = this.buildingCache.get(type);
    if (cached) return cached;
    const def = BUILDING_DEFS[type];
    const g = new Graphics();
    const meta = drawBuilding(g, type, def.w, def.h);
    const bounds = g.getLocalBounds();
    const texture = this.renderer.generateTexture({ target: g, antialias: false });
    g.destroy();
    // Pole art is authored around the tile center; shift it into V-space.
    const shiftY = type === 'powerPole' ? HALF_H : 0;
    const entry: BuildingTexture = {
      texture,
      offsetX: bounds.minX,
      offsetY: bounds.minY + shiftY,
      attachX: meta.attachX,
      attachY: meta.attachY + shiftY,
      anim: meta.anim ? { ...meta.anim, y: meta.anim.y + shiftY } : null,
    };
    this.buildingCache.set(type, entry);
    return entry;
  }

  /** Accessory textures for animated touches. */
  accessory(kind: 'sign' | 'puff' | 'drip'): Texture {
    const key = `acc:${kind}`;
    const cached = this.accessoryCache.get(key);
    if (cached) return cached;
    const texture = this.make((g) => {
      if (kind === 'sign') {
        g.rect(2, 0, 2, 3).fill(0x2a1f16);
        g.rect(0, 3, 9, 6).fill(0xe8c170);
        g.rect(0, 3, 9, 6).stroke({ width: 1, color: 0x2a1f16, alpha: 0.8 });
        g.rect(2, 5, 5, 1).fill(0x8a4a2a);
      } else if (kind === 'puff') {
        g.circle(3, 3, 3).fill({ color: 0xd8d8d2, alpha: 0.85 });
        g.circle(5.5, 2, 2).fill({ color: 0xe8e8e2, alpha: 0.7 });
      } else {
        g.rect(0, 0, 2, 3).fill(0x8fc0dd);
      }
    });
    this.accessoryCache.set(key, texture);
    return texture;
  }

  /** Footprint-outline parallelogram for attention halos, white for tinting. */
  halo(w: number, h: number): Texture {
    const key = `halo:${w}:${h}`;
    const cached = this.accessoryCache.get(key);
    if (cached) return cached;
    const texture = this.make((g) => {
      const n = [h * HALF_W, 0] as const;
      const e = [(h + w) * HALF_W, w * HALF_H] as const;
      const s = [w * HALF_W, (w + h) * HALF_H] as const;
      const west = [0, h * HALF_H] as const;
      g.poly([...n, ...e, ...s, ...west]).stroke({ width: 3, color: 0xffffff });
      // Inner echo reads as a glow at 1x nearest-neighbor.
      const cy = (w + h) * HALF_H * 0.5;
      const cx = (w + h) * HALF_W * 0.5;
      const inset = (p: readonly [number, number]): [number, number] => [
        p[0] + (cx - p[0]) * 0.12,
        p[1] + (cy - p[1]) * 0.12,
      ];
      g.poly([...inset(n), ...inset(e), ...inset(s), ...inset(west)]).stroke({
        width: 1,
        color: 0xffffff,
        alpha: 0.45,
      });
    });
    this.accessoryCache.set(key, texture);
    return texture;
  }

  /** 12x12 problem badges on a dark chip so they read at distance. */
  badge(kind: 'bolt' | 'boltHalf' | 'boltOff' | 'drop' | 'person' | 'sack' | 'crates'): Texture {
    const key = `badge:${kind}`;
    const cached = this.accessoryCache.get(key);
    if (cached) return cached;
    const texture = this.make((g) => {
      g.rect(0, 0, 12, 12).fill({ color: 0x1a1410, alpha: 0.78 });
      g.rect(0, 0, 12, 12).stroke({ width: 1, color: 0xe07a6a });
      const bolt = (color: number, alpha = 1): void => {
        g.poly([7, 1, 4, 6, 6, 6, 4, 11, 9, 5, 6.5, 5]).fill({ color, alpha });
      };
      if (kind === 'bolt') {
        bolt(0xf2c14e);
      } else if (kind === 'boltHalf') {
        bolt(0xf2c14e, 0.45);
        g.rect(6, 0, 6, 12).fill({ color: 0x1a1410, alpha: 0.6 });
      } else if (kind === 'boltOff') {
        bolt(0x8a8a8a);
        g.moveTo(2, 2).lineTo(10, 10).stroke({ width: 1.5, color: 0xe05252 });
        g.moveTo(10, 2).lineTo(2, 10).stroke({ width: 1.5, color: 0xe05252 });
      } else if (kind === 'drop') {
        g.poly([6, 1.5, 3.6, 6.5, 8.4, 6.5]).fill(0x63a8da);
        g.circle(6, 7.5, 3).fill(0x63a8da);
      } else if (kind === 'person') {
        g.circle(6, 3.5, 2).fill(0xf0d0b0);
        g.rect(3.5, 6, 5, 5).fill(0x9a9a9a);
      } else if (kind === 'sack') {
        g.ellipse(6, 8, 3.5, 2.8).fill(0xe8c170);
        g.rect(5.2, 3.5, 1.6, 2).fill(0xe8c170);
        g.moveTo(4.5, 4.5).lineTo(7.5, 4.5).stroke({ width: 1, color: 0x8a6a3a });
      } else {
        g.rect(2, 6, 5, 5).fill(0xb09468);
        g.rect(2, 6, 5, 5).stroke({ width: 1, color: 0x5a4630 });
        g.rect(6, 2, 5, 5).fill(0xb09468);
        g.rect(6, 2, 5, 5).stroke({ width: 1, color: 0x5a4630 });
      }
    });
    this.accessoryCache.set(key, texture);
    return texture;
  }

  /** ~26x14 side-view vehicle, facing +x; mirror with scale.x for the return leg. */
  vehicle(kind: 'oreTruck' | 'goodsWagon'): Texture {
    const key = `veh:${kind}`;
    const cached = this.accessoryCache.get(key);
    if (cached) return cached;
    const body = kind === 'oreTruck' ? 0x8a4a2a : 0xb09468;
    const cab = kind === 'oreTruck' ? 0x9a5a3a : 0xc0a478;
    const texture = this.make((g) => {
      g.rect(0, 5, 18, 5).fill(body);
      g.rect(17, 2, 7, 8).fill(cab);
      g.rect(19, 3.5, 3.5, 3).fill(0xbcd8e8);
      if (kind === 'oreTruck') {
        for (const [cx, cy] of [[4, 4.5], [9, 3.5], [14, 4.5]] as const) {
          g.circle(cx, cy, 2.5).fill(0x8a6f54);
        }
      } else {
        for (const cx of [4, 9, 14] as const) g.ellipse(cx, 4, 2.6, 2).fill(0xe8c170);
      }
      for (const cx of [5, 19] as const) {
        g.circle(cx, 11.5, 2.5).fill(0x2a2a2a);
        g.circle(cx, 11.5, 1).fill(0x555555);
      }
    });
    this.accessoryCache.set(key, texture);
    return texture;
  }

  /** ~14x10 mini-longhorn led by a hauler; two-frame gait like the peeps. */
  cow(frame: 0 | 1): Texture {
    const key = `cow:${frame}`;
    const cached = this.peepCache.get(key);
    if (cached) return cached;
    const texture = this.make((g) => {
      g.ellipse(6, 5, 5, 3).fill(0x8a6a4a);
      g.ellipse(4.5, 4, 2.5, 1.8).fill(0x7a5a40);
      g.ellipse(11, 4, 2, 1.8).fill(0x6e523a);
      g.moveTo(9, 2).lineTo(13, 2).stroke({ width: 1, color: 0xe4d5b0 });
      const legs = frame === 0 ? ([2, 4, 8, 10] as const) : ([1.5, 4.5, 7.5, 10.5] as const);
      for (let index = 0; index < legs.length; index += 1) {
        const tall = frame === 0 || index % 2 === 0;
        g.rect(legs[index]!, 7.5, 1, tall ? 3 : 2).fill(0x5a4630);
      }
    });
    this.peepCache.set(key, texture);
    return texture;
  }

  /** Ore speck bands overlaid on cliff faces: SW/SE shape x iron/gold. */
  faceOre(face: 'sw' | 'se', ore: 'iron' | 'gold'): Texture {
    const key = `faceOre:${face}:${ore}`;
    const cached = this.accessoryCache.get(key);
    if (cached) return cached;
    const texture = this.make((g) => {
      // Invisible base matching the face parallelogram keeps positioning identical.
      if (face === 'sw') g.poly([0, 0, HALF_W, HALF_H, HALF_W, HALF_H + 17, 0, 17]).fill({ color: 0xffffff, alpha: 0.001 });
      else g.poly([0, HALF_H, HALF_W, 0, HALF_W, 17, 0, HALF_H + 17]).fill({ color: 0xffffff, alpha: 0.001 });
      const skew = (x: number): number => (face === 'sw' ? (x / HALF_W) * HALF_H : HALF_H - (x / HALF_W) * HALF_H);
      const spots: readonly (readonly [number, number])[] =
        ore === 'iron'
          ? [[5, 5], [13, 9], [22, 4], [27, 11], [9, 12]]
          : [[4, 6], [11, 4], [18, 10], [25, 5], [29, 12], [15, 13]];
      for (const [x, dy] of spots) {
        const y = skew(x) + dy;
        if (ore === 'iron') {
          g.rect(x, y, 2.5, 1.5).fill(0x3a2a20);
        } else {
          g.rect(x, y, 2.5, 1.5).fill(0xf2c14e);
          g.rect(x + 0.5, y - 1, 1, 1).fill(0xfff2c8);
        }
      }
    });
    this.accessoryCache.set(key, texture);
    return texture;
  }
}
